// src/pages/profilepage.jsx

import React, { useState, useEffect } from 'react';
import { User, Phone, Edit2, Shield, Star, Calendar, LogOut, Save, X, Eye, EyeOff, AlertCircle, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout'; // Adjusted import path based on your structure

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [ratingSummary, setRatingSummary] = useState({ dimensions: {} });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [editData, setEditData] = useState({
    name: '',
    gender: '',
    password: '',
    family_friendly: false,
    smoke_free: false
  });
  const [showPassword, setShowPassword] = useState(false);

  // Fetch user profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    const token = localStorage.getItem('journi_token');

    if (!token) {
      // The Layout component handles a general logout flow, but this ensures initial check/redirect
      window.location.href = '/login';
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.user) {
        setUser(data.user);
        setRatingSummary(data.rating_summary || { dimensions: {} });
        setEditData({
          name: data.user.name,
          gender: data.user.gender,
          password: '',
          family_friendly: !!data.user.family_friendly,
          smoke_free: !!data.user.smoke_free
        });
      } else {
        localStorage.removeItem('journi_token');
        localStorage.removeItem('journi_user');
        window.location.href = '/login';
      }
    } catch (err) {
      setError('Failed to load profile');
    }

    setLoading(false);
  };

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    const token = localStorage.getItem('journi_token');

    const updatePayload = {};
    if (editData.name !== user.name) updatePayload.name = editData.name;
    if (editData.gender !== user.gender) updatePayload.gender = editData.gender;
    if (editData.password) updatePayload.password = editData.password;
    if (!!editData.family_friendly !== !!user.family_friendly) updatePayload.family_friendly = editData.family_friendly;
    if (!!editData.smoke_free !== !!user.smoke_free) updatePayload.smoke_free = editData.smoke_free;

    if (Object.keys(updatePayload).length === 0) {
      setError('No changes to save');
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUser(data.user);
        if (data.rating_summary) setRatingSummary(data.rating_summary);
        localStorage.setItem('journi_user', JSON.stringify(data.user));
        
        if (data.token) {
          localStorage.setItem('journi_token', data.token);
        }

        setSuccess('Profile updated successfully!');
        setEditMode(false);
        setEditData({...editData, password: ''});
      } else {
        setError(data.error?.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }

    setSaving(false);
  };

  // Logout is handled by the Layout component's button logic,
  // but we keep the logic here just in case the component needed it for another purpose.
  // const handleLogout = () => {
  //   localStorage.removeItem('journi_token');
  //   localStorage.removeItem('journi_user');
  //   window.location.href = '/login';
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
            <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-emerald-800">{success}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                <p className="text-gray-600 flex items-center gap-2 mt-1">
                  <Phone className="w-4 h-4" />
                  {user.phone}
                </p>
              </div>
            </div>
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 transition"
              >
                <Edit2 className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>

          {/* Profile Info */}
          {!editMode ? (
            <div className="space-y-6">
              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Gender</label>
                <p className="text-lg text-gray-900 capitalize">{user.gender}</p>
              </div>

              {/* Member Since */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
                <p className="text-lg text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  {new Date(user.created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              {/* Verification Status */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Verification</label>
                <div className="flex items-center gap-2">
                  {user.verified ? (
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Verified
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                      Not Verified
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Trust Badges</label>
                <div className="flex flex-wrap gap-2">
                  {user.family_friendly == 1 && (
                    <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm font-medium">
                      Family Friendly {user.family_friendly_confirmations ? `· confirmed by ${user.family_friendly_confirmations}` : ''}
                    </span>
                  )}
                  {user.smoke_free == 1 && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      Smoke-Free {user.smoke_free_confirmations ? `· confirmed by ${user.smoke_free_confirmations}` : ''}
                    </span>
                  )}
                  {user.open_reports_count > 0 && (
                    <span className="px-3 py-1 bg-rose-50 text-rose-700 rounded-full text-sm font-medium">
                      {user.open_reports_count} open report{user.open_reports_count === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              </div>

              {ratingSummary?.dimensions && Object.keys(ratingSummary.dimensions).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Rating Breakdown</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(ratingSummary.dimensions).map(([key, value]) => (
                      <div key={key} className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="mt-1 text-lg font-semibold text-gray-900">
                          {value.average}★ <span className="text-sm font-normal text-gray-500">({value.count})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-200">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{user.rides_completed_count || user.total_rides || 0}</div>
                  <div className="text-sm text-gray-600">Total Rides</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-1">
                    {ratingSummary?.overall_rating || user.rating || 0}
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  </div>
                  <div className="text-sm text-gray-600">{ratingSummary?.ratings_count || user.total_reviews || 0} ratings</div>
                </div>
              </div>
            </div>
          ) : (
            // Edit Mode
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={editData.name}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setEditData({...editData, gender: 'male'})}
                    className={`py-3 rounded-lg border-2 font-medium transition ${
                      editData.gender === 'male'
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditData({...editData, gender: 'female'})}
                    className={`py-3 rounded-lg border-2 font-medium transition ${
                      editData.gender === 'female'
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setEditData({ ...editData, family_friendly: !editData.family_friendly })}
                  className={`py-3 rounded-lg border-2 font-medium transition ${
                    editData.family_friendly
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Family Friendly
                </button>
                <button
                  type="button"
                  onClick={() => setEditData({ ...editData, smoke_free: !editData.smoke_free })}
                  className={`py-3 rounded-lg border-2 font-medium transition ${
                    editData.smoke_free
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Smoke-Free
                </button>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password (Optional)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={editData.password}
                    onChange={handleEditChange}
                    placeholder="Leave blank to keep current password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Changing password will log you out and require re-login
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditData({
                      name: user.name,
                      gender: user.gender,
                      password: '',
                      family_friendly: !!user.family_friendly,
                      smoke_free: !!user.smoke_free
                    });
                    setError('');
                    setSuccess('');
                  }}
                  disabled={saving}
                  className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 flex items-center gap-2"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* API Info */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-semibold text-blue-900 mb-2">🎉 Connected to Backend API</p>
          <p className="text-xs text-blue-800">✅ GET /api/v1/users/me - Profile loaded</p>
          <p className="text-xs text-blue-800">✅ PUT /api/v1/users/me - Updates working</p>
          <p className="text-xs text-blue-800">✅ JWT automatically refreshed on password change</p>
        </div>
      </div>
    </Layout>
  );
}
